import { useState, useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, Users, AlertTriangle, RefreshCw, Calendar, Hash } from 'lucide-react';
import { apiService, isWriteEnabled, queryKeys } from '@/services/api';
import { SortOption, type TableFilters, type DivisionPlayer, type DivisionInfo } from '@/types/api';
import { toast } from '@/components/ui/Toaster';
import { saveRecentDivision } from '@/pages/Dashboard';
import PlayerTable from '@/components/PlayerTable';
import ControlPanel from '@/components/ControlPanel';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { inferAvailableGameTabs, cleanDivisionName, type GameTabKey } from '@/utils/divisionGameTabs';

export default function RankingsPage() {
  const { divisionId } = useParams<{ divisionId: string }>();
  const [activeGameTab, setActiveGameTab] = useState<GameTabKey>('8ball');
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const canRefresh = isWriteEnabled;

  const [filters, setFilters] = useState<TableFilters>({
    search: '',
    gameType: 'all',
    sortBy: SortOption.EIGHTBALL_CSR,
    sortDirection: 'desc',
    groupByTeam: false,
  });

  const refreshMutation = useMutation({
    mutationFn: () => apiService.refreshDivision(divisionId!, { force: true }),
    onMutate: () => setIsRefreshing(true),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.divisionRankings(divisionId!, filters.groupByTeam) });
      toast.success('Rankings refreshed. Match history updating in background.');
    },
    onError: (err: Error) => {
      toast.error(`Refresh failed: ${err.message}`);
    },
    onSettled: () => setIsRefreshing(false),
  });

  // Track this division in recents
  useEffect(() => {
    if (divisionId) saveRecentDivision(divisionId);
  }, [divisionId]);

  // Update default sort when game tab changes
  useEffect(() => {
    const sortMap: Record<string, SortOption> = {
      '8ball': SortOption.EIGHTBALL_CSR,
      '9ball': SortOption.NINEBALL_CSR,
      '10ball': SortOption.TENBALL_CSR,
    };
    setFilters(f => ({ ...f, sortBy: sortMap[activeGameTab] || SortOption.EIGHTBALL_CSR }));
  }, [activeGameTab]);

  const {
    data: rankings,
    isLoading,
    error,
  } = useQuery({
    queryKey: queryKeys.divisionRankings(divisionId!, filters.groupByTeam),
    queryFn: () => apiService.getDivisionRankings(divisionId!, filters.groupByTeam),
    enabled: !!divisionId,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      // Don't retry 404s (no cached data) — response is instant and deterministic
      if (error?.message?.includes('No cached rankings')) return false;
      return failureCount < 1;
    },
  });

  // Fetch division metadata for the header info bar
  const { data: divisions } = useQuery({
    queryKey: queryKeys.divisions,
    queryFn: () => apiService.getDivisions(),
    staleTime: 5 * 60 * 1000,
  });

  const divisionInfo = useMemo(() => {
    if (!divisions || !divisionId) return null;
    return divisions.find((d: DivisionInfo) => String(d.division_id) === divisionId) || null;
  }, [divisions, divisionId]);

  const availableGameTabs = useMemo(
    () => inferAvailableGameTabs(divisionInfo?.name),
    [divisionInfo?.name]
  );

  // A division is "current" if its last match is within the last 45 days.
  // For current divisions, the live rankings CSR is authoritative;
  // period stats (from DB match history) may be stale.
  const isDivisionCurrent = useMemo(() => {
    if (!divisionInfo?.last_match_date) return false;
    const lastMatch = new Date(divisionInfo.last_match_date);
    const daysSinceLastMatch = (Date.now() - lastMatch.getTime()) / (1000 * 60 * 60 * 24);
    return daysSinceLastMatch <= 45;
  }, [divisionInfo]);

  // Fetch period CSRs only for historical divisions
  const { data: periodStats } = useQuery({
    queryKey: queryKeys.divisionPeriodStats(divisionId!),
    queryFn: () => apiService.getDivisionPeriodStats(divisionId!),
    staleTime: 5 * 60 * 1000,
    enabled: !!divisionId && !isDivisionCurrent,
  });

  useEffect(() => {
    if (!availableGameTabs.includes(activeGameTab)) {
      setActiveGameTab(availableGameTabs[0]);
    }
  }, [activeGameTab, availableGameTabs]);

  const getSortValue = (player: DivisionPlayer, sortBy: SortOption): any => {
    switch (sortBy) {
      case SortOption.NAME: return player.name.toLowerCase();
      case SortOption.TEAM: return (player.team_name || '').toLowerCase();
      case SortOption.EIGHTBALL_CSR: return player.games['8ball']?.skill_level || 0;
      case SortOption.NINEBALL_CSR: return player.games['9ball']?.skill_level || 0;
      case SortOption.TENBALL_CSR: return player.games['10ball']?.skill_level || 0;
      case SortOption.EIGHTBALL_CLASS: return (player.games['8ball']?.skill_class || 'ZZZ').toLowerCase();
      case SortOption.NINEBALL_CLASS: return (player.games['9ball']?.skill_class || 'ZZZ').toLowerCase();
      case SortOption.TENBALL_CLASS: return (player.games['10ball']?.skill_class || 'ZZZ').toLowerCase();
      case SortOption.EIGHTBALL_RANK: return player.games['8ball']?.local_rank ? parseInt(String(player.games['8ball'].local_rank)) : 999999;
      case SortOption.NINEBALL_RANK: return player.games['9ball']?.local_rank ? parseInt(String(player.games['9ball'].local_rank)) : 999999;
      case SortOption.TENBALL_RANK: return player.games['10ball']?.local_rank ? parseInt(String(player.games['10ball'].local_rank)) : 999999;
      case SortOption.EIGHTBALL_NATIONAL: return player.games['8ball']?.national_rank ? parseInt(String(player.games['8ball'].national_rank)) : 999999;
      case SortOption.NINEBALL_NATIONAL: return player.games['9ball']?.national_rank ? parseInt(String(player.games['9ball'].national_rank)) : 999999;
      case SortOption.TENBALL_NATIONAL: return player.games['10ball']?.national_rank ? parseInt(String(player.games['10ball'].national_rank)) : 999999;
      case SortOption.MATCHES_8BALL: return player.games['8ball']?.matches_played || 0;
      case SortOption.MATCHES_9BALL: return player.games['9ball']?.matches_played || 0;
      case SortOption.MATCHES_10BALL: return player.games['10ball']?.matches_played || 0;
      default: return player.name.toLowerCase();
    }
  };

  const filteredPlayers = useMemo(() => {
    if (!rankings?.players) return [];
    let filtered = rankings.players.filter((player) => {
      if (filters.search) {
        const s = filters.search.toLowerCase();
        if (!player.name.toLowerCase().includes(s) && !player.team_name?.toLowerCase().includes(s) && !player.player_id.includes(s)) return false;
      }
      return true;
    });
    filtered.sort((a, b) => {
      const aVal = getSortValue(a, filters.sortBy);
      const bVal = getSortValue(b, filters.sortBy);
      const cmp = typeof aVal === 'string' ? aVal.localeCompare(bVal) : (aVal || 0) - (bVal || 0);
      return filters.sortDirection === 'desc' ? -cmp : cmp;
    });
    return filtered;
  }, [rankings?.players, filters.search, filters.sortBy, filters.sortDirection]);

  if (!divisionId) {
    return (
      <div className="flex-1 flex items-center justify-center p-12">
        <div className="text-center">
          <AlertTriangle className="w-10 h-10 text-red-400 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-slate-900 mb-1">Invalid Division</h1>
          <p className="text-slate-500 mb-4">No division ID provided.</p>
          <Link to="/" className="text-brand-600 hover:text-brand-700 font-medium text-sm">← Back to Dashboard</Link>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center p-12">
        <LoadingSpinner size="lg" message="Loading division rankings..." />
      </div>
    );
  }

  if (error) {
    const isNotCached = error.message?.includes('No cached rankings') || error.message?.includes('404');
    const isTimeout = error.message?.includes('timed out') || error.message?.includes('504');
    const isDataUnavailable = isNotCached || isTimeout;
    return (
      <div className="flex-1 flex items-center justify-center p-12">
        <div className="text-center">
          <AlertTriangle className={`w-10 h-10 mx-auto mb-3 ${isDataUnavailable ? 'text-amber-400' : 'text-red-400'}`} />
          <h1 className="text-xl font-bold text-slate-900 mb-1">
            {isDataUnavailable ? 'Rankings Not Available' : 'Error Loading Data'}
          </h1>
          <p className="text-slate-500 mb-4">
            {isDataUnavailable
              ? 'Rankings for this division haven\'t been fetched yet. You can fetch them now — this may take a few minutes.'
              : error.message}
          </p>
          <div className="flex gap-3 justify-center">
            {canRefresh && isDataUnavailable && (
              <button
                onClick={() => refreshMutation.mutate()}
                disabled={isRefreshing}
                className="px-4 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 transition-colors disabled:opacity-50"
              >
                {isRefreshing ? 'Fetching Data…' : 'Fetch Rankings'}
              </button>
            )}
            {!isDataUnavailable && (
              <button onClick={() => window.location.reload()} className="px-4 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 transition-colors">
                Retry
              </button>
            )}
            <Link to="/" className="px-4 py-2 border border-slate-200 text-slate-600 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors">
              Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!rankings) return null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Breadcrumb + Title */}
      <div className="mb-5">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-slate-600 transition-colors mb-2">
          <ChevronLeft className="w-3.5 h-3.5" />
          Dashboard
        </Link>
        <div className="flex flex-wrap items-center gap-2 md:gap-3">
          <h1 className="text-xl md:text-2xl font-bold text-slate-900">
            {cleanDivisionName(divisionInfo?.name ?? null) || `Division ${divisionId}`}
          </h1>
          <span className="flex items-center gap-1.5 text-sm text-slate-400">
            <Users className="w-4 h-4" />
            {rankings.total_players} players
          </span>
          {isWriteEnabled && (
            <button
              onClick={() => refreshMutation.mutate()}
              disabled={isRefreshing || !canRefresh}
              className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-slate-500 hover:text-brand-600 hover:bg-brand-50 active:bg-brand-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Refresh all player data"
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </button>
          )}
          <Link
            to={`/division/${divisionId}/matchup`}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Matchup optimizer"
          >
            ⚔️ Matchups
          </Link>
        </div>
        {/* Division info bar */}
        {divisionInfo && (
          <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-slate-500">
            {divisionInfo.name && (
              <span className="flex items-center gap-1.5">
                <Hash className="w-3.5 h-3.5" />
                {divisionId}
              </span>
            )}
            {divisionInfo.first_match_date && divisionInfo.last_match_date && (
              <span className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" />
                {new Date(divisionInfo.first_match_date.includes('T') ? divisionInfo.first_match_date : divisionInfo.first_match_date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                {' – '}
                {new Date(divisionInfo.last_match_date.includes('T') ? divisionInfo.last_match_date : divisionInfo.last_match_date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            )}
            <span>{divisionInfo.match_count} matches</span>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="mb-4">
        <ControlPanel
          filters={filters}
          onFiltersChange={setFilters}
          activeGameTab={activeGameTab}
          onGameTabChange={(tab) => setActiveGameTab(tab as GameTabKey)}
          availableGameTabs={availableGameTabs}
        />
      </div>

      {/* Table */}
      <PlayerTable
        players={filteredPlayers}
        filters={filters}
        onFiltersChange={setFilters}
        activeGameTab={activeGameTab}
        divisionId={divisionId}
        periodStats={isDivisionCurrent ? undefined : periodStats?.player_stats}
      />
    </div>
  );
}
